import React, { useState, useEffect, useRef } from 'react';
import './CustomVideoPlayer.css';

const CustomVideoPlayer = ({ videoRef, fileName, isFullscreen, onFullscreenToggle }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  
  const [subtitles, setSubtitles] = useState([]);
  const [selectedSubtitle, setSelectedSubtitle] = useState(-1);
  const [showSubtitleMenu, setShowSubtitleMenu] = useState(false);
  const [currentCue, setCurrentCue] = useState('');
  
  const [subtitleSize, setSubtitleSize] = useState(18);
  const [subtitleBg, setSubtitleBg] = useState(true);
  const [showSubtitleSettings, setShowSubtitleSettings] = useState(false);
  
  // ⭐ Progress hover
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPercent, setHoverPercent] = useState(0);
  
  const progressRef = useRef(null);
  const controlsTimeout = useRef(null);
  const subtitleMenuRef = useRef(null);
  const settingsMenuRef = useRef(null);

  const getVideo = () => videoRef?.current;

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const parseSubtitleFile = (text) => {
    const cues = [];
    const blocks = text.replace(/\r\n/g, '\n').split('\n\n');
    blocks.forEach(block => {
      const lines = block.trim().split('\n');
      if (lines.length < 2) return;
      const timeLine = lines.find(l => l.includes('-->'));
      if (!timeLine) return;
      const timeMatch = timeLine.match(/([\d:,]+)\s*-->\s*([\d:,]+)/);
      if (!timeMatch) return;
      const timeIndex = lines.indexOf(timeLine);
      const cueText = lines.slice(timeIndex + 1).join('\n').replace(/<[^>]+>/g, '').trim();
      cues.push({ start: timeToSeconds(timeMatch[1].replace(',', '.')), end: timeToSeconds(timeMatch[2].replace(',', '.')), text: cueText });
    });
    return cues;
  };

  const timeToSeconds = (t) => {
    const parts = t.split(':');
    if (parts.length === 3) return +parts[0]*3600 + +parts[1]*60 + parseFloat(parts[2]);
    return +parts[0]*60 + parseFloat(parts[1]);
  };

  useEffect(() => {
    if (selectedSubtitle < 0 || !subtitles[selectedSubtitle]) { setCurrentCue(''); return; }
    const sub = subtitles[selectedSubtitle];
    if (sub.embedded) { setCurrentCue(''); return; }
    const cues = sub.cues || [];
    const cue = cues.find(c => currentTime >= c.start && currentTime <= c.end);
    setCurrentCue(cue ? cue.text : '');
  }, [currentTime, selectedSubtitle, subtitles]);

  useEffect(() => {
    const video = getVideo();
    if (!video) return;
    const h = () => { if (!isDragging) setCurrentTime(video.currentTime); };
    const m = () => setDuration(video.duration);
    video.addEventListener('timeupdate', h);
    video.addEventListener('loadedmetadata', m);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));
    video.addEventListener('ended', () => setIsPlaying(false));
    return () => { video.removeEventListener('timeupdate', h); video.removeEventListener('loadedmetadata', m); };
  }, [isDragging]);

  useEffect(() => {
    const h = (e) => {
      if (subtitleMenuRef.current && !subtitleMenuRef.current.contains(e.target)) setShowSubtitleMenu(false);
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(e.target)) setShowSubtitleSettings(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ⭐ Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const video = getVideo();
      if (!video) return;
      
      // ⭐ اگه فوکوس روی input یا textarea هست، کیبورد shortcuts کار نکنه
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) {
        return;
      }
      
      switch(e.key) {
        case ' ':
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skip(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skip(10);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, duration]);


  const handleSubtitleAdd = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const cues = parseSubtitleFile(ev.target.result);
      const label = file.name.replace(/\.(vtt|srt)$/i, '');
      setSubtitles(prev => [...prev, { label, cues, embedded: false }]);
      setSelectedSubtitle(subtitles.length);
    };
    reader.readAsText(file);
    setShowSubtitleMenu(false);
  };

  const togglePlay = () => { const v = getVideo(); if (!v) return; isPlaying ? v.pause() : v.play().catch(); };
  const skip = (s) => { const v = getVideo(); if (!v) return; v.currentTime = Math.max(0, Math.min(v.currentTime + s, duration)); };
  const toggleMute = () => { const v = getVideo(); if (!v) return; v.muted = !v.muted; setIsMuted(v.muted); };
  const handleVolumeChange = (e) => { const v = getVideo(); if (!v) return; const val = parseFloat(e.target.value); v.volume = val; setVolume(val); setIsMuted(val === 0); };
  
  const handleProgressClick = (e) => {
    const v = getVideo();
    if (!v || !progressRef.current) return;
    v.currentTime = ((e.clientX - progressRef.current.getBoundingClientRect().left) / progressRef.current.getBoundingClientRect().width) * duration;
  };

  const handleProgressHover = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    setHoverPercent(percent * 100);
    setHoverTime(percent * duration);
  };

  return (
    <div className={`custom-video-player ${isFullscreen ? 'fullscreen' : ''} ${!showControls && isPlaying ? 'hide-controls' : ''}`}
      onMouseMove={() => { setShowControls(true); if (controlsTimeout.current) clearTimeout(controlsTimeout.current); if (isPlaying) controlsTimeout.current = setTimeout(() => setShowControls(false), 3000); }}
      onMouseLeave={() => { if (isPlaying) setShowControls(false); }}>
      
      <video ref={videoRef} className="video-element" preload="auto" crossOrigin="anonymous"
        style={{ width: '100%', height: '100%', display: fileName ? 'block' : 'none' }} onClick={togglePlay} />

      {currentCue && (
        <div className="subtitle-text" style={{ fontSize: `${subtitleSize}px`, background: subtitleBg ? 'rgba(0,0,0,0.8)' : 'transparent', padding: subtitleBg ? '6px 14px' : '2px 6px', bottom: `${Math.max(50, subtitleSize + 40)}px` }}>
          {currentCue.split('\n').map((line, i) => <span key={i}>{line}<br/></span>)}
        </div>
      )}

      {!isPlaying && fileName && (
        <div className="big-play-button" onClick={togglePlay}>
          <svg viewBox="0 0 24 24" width="64" height="64" fill="white"><path d="M8 5v14l11-7z"/></svg>
        </div>
      )}

      {fileName && (
        <div className={`video-controls ${showControls ? 'visible' : ''}`}>
          {/* ⭐ Progress Bar با tooltip */}
          <div ref={progressRef} className="progress-bar" onClick={handleProgressClick}
            onMouseMove={handleProgressHover}
            onMouseLeave={() => setHoverTime(null)}>
            <div className="progress-filled" style={{ width: `${duration>0?(currentTime/duration)*100:0}%` }}></div>
            {hoverTime !== null && !isNaN(hoverTime) && (
              <div className="progress-tooltip" style={{ left: `${hoverPercent}%` }}>{formatTime(hoverTime)}</div>
            )}
          </div>

          <div className="controls-bottom">
            <div className="controls-left">
              <button className="control-btn" onClick={togglePlay}>
                {isPlaying ? <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg> : <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>}
              </button>
              <button className="control-btn" onClick={() => skip(-10)}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/></svg>
                <span style={{fontSize:'10px',fontWeight:700,marginLeft:'2px'}}>10</span>
              </button>
              <button className="control-btn" onClick={() => skip(10)}>
                <span style={{fontSize:'10px',fontWeight:700,marginRight:'2px'}}>10</span>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/></svg>
              </button>
              <button className="control-btn" onClick={toggleMute}>
                {isMuted || volume===0 ? <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg> : <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>}
              </button>
              <input 
  type="range" 
  className="volume-slider" 
  min="0" 
  max="1" 
  step="0.05" 
  value={isMuted ? 0 : volume} 
  onChange={handleVolumeChange}
  style={{ '--volume-percent': `${(isMuted ? 0 : volume) * 100}%` }}
  title="Volume"
/>

              <div className="subtitle-wrapper" ref={subtitleMenuRef}>
                <button className={`control-btn ${selectedSubtitle>=0?'active':''}`} onClick={(e)=>{e.stopPropagation();setShowSubtitleMenu(!showSubtitleMenu);}} title="Subtitles">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 6h16v12H4V6zm2 4h2v2H6v-2zm0 4h8v2H6v-2zm10 0h2v2h-2v-2zm-6-4h8v2h-8v-2z"/></svg>
                </button>
                {showSubtitleMenu && (
                  <div className="subtitle-menu">
                    <div className="subtitle-menu-header">Subtitles</div>
                    <div className={`subtitle-option ${selectedSubtitle===-1?'selected':''}`} onClick={()=>{setSelectedSubtitle(-1);setShowSubtitleMenu(false);}}><span>Off</span>{selectedSubtitle===-1&&<span className="check">✓</span>}</div>
                    {subtitles.map((sub,i)=>(
                      <div key={i} className={`subtitle-option ${selectedSubtitle===i?'selected':''}`} onClick={()=>{setSelectedSubtitle(i);setShowSubtitleMenu(false);}}><span>{sub.label}</span>{selectedSubtitle===i&&<span className="check">✓</span>}</div>
                    ))}
                    <div className="subtitle-menu-header" style={{marginTop:'4px'}}>Settings</div>
                    <div className="subtitle-option" onClick={()=>{setShowSubtitleSettings(true);setShowSubtitleMenu(false);}}><span>⚙️ Subtitle Settings</span></div>
                    <label className="subtitle-option add-subtitle"><span>+ Add Subtitle</span><input type="file" accept=".vtt,.srt" onChange={handleSubtitleAdd} style={{display:'none'}}/></label>
                  </div>
                )}
              </div>

              {showSubtitleSettings && (
                <div className="subtitle-settings-overlay" onClick={()=>setShowSubtitleSettings(false)}>
                  <div className="subtitle-settings-modal" ref={settingsMenuRef} onClick={(e)=>e.stopPropagation()}>
                    <div className="subtitle-settings-header">Subtitle Settings</div>
                    <div className="settings-row">
                      <span>Font Size</span>
                      <div className="settings-controls">
                        <button className="settings-btn" onClick={()=>setSubtitleSize(s=>Math.max(12,s-2))}>A-</button>
                        <span className="settings-value">{subtitleSize}px</span>
                        <button className="settings-btn" onClick={()=>setSubtitleSize(s=>Math.min(40,s+2))}>A+</button>
                      </div>
                    </div>
                    <div className="settings-row">
                      <span>Background</span>
                      <button className={`settings-btn ${subtitleBg?'active':''}`} onClick={()=>setSubtitleBg(!subtitleBg)}>{subtitleBg ? 'ON' : 'OFF'}</button>
                    </div>
                    <button className="settings-close-btn" onClick={()=>setShowSubtitleSettings(false)}>Done</button>
                  </div>
                </div>
              )}

              <span className="time-display">{formatTime(currentTime)} / {formatTime(duration)}</span>
            </div>
            <div className="controls-right">
              <button className="control-btn" onClick={onFullscreenToggle}>
                {isFullscreen ? <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg> : <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default CustomVideoPlayer;